/**
 * Staff insights — hours, wages, and the tip pool for the selected dates.
 * Tips from paid orders are split in proportion to hours worked.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { Database } from '@cafe-pos/supabase';
import { formatAmd, resolveAppLocale } from '../locale.js';
import { sumPaidTips } from '../lib/paidTips.js';
import {
  formatHours,
  splitAmount,
  wageForRange,
  type PayType,
  type PunchSpan,
  type RatePeriod,
} from '../lib/wages.js';
import { supabase } from '../supabase.js';
import {
  Callout,
  CHART_TOOLTIP,
  DateRange,
  PageHeader,
  Stat,
  Stats,
  chartColor,
  resolveDateRangeDates,
  type DateRangeValue,
} from '../ui/index.js';

type StaffRow = Database['public']['Tables']['staff']['Row'];
type PayRow = Database['public']['Tables']['staff_pay']['Row'];
type RateRow = Database['public']['Tables']['staff_rates']['Row'];
type EntryRow = Database['public']['Tables']['time_entries']['Row'];

function asPayType(value: string | undefined): PayType {
  if (value === 'shift' || value === 'monthly') return value;
  return 'hourly';
}

interface EarningRow {
  id: string;
  name: string;
  minutes: number;
  wageAmd: number;
  tipAmd: number;
  totalAmd: number;
  share: number;
}

export function StaffInsightsView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const money = (n: number) => formatAmd(n, i18n.language);
  const [range, setRange] = useState<DateRangeValue>({ preset: 'this_month' });
  const [rows, setRows] = useState<EarningRow[]>([]);
  const [tipPool, setTipPool] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId || !supabase) return;
    if (range.preset === 'custom' && (!range.from || !range.to)) return;
    const db = supabase;
    const [from, to] = resolveDateRangeDates(range);
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const startIso = from.toISOString();
        const endIso = to.toISOString();
        const [staffRes, payRes, rateRes, closedRes, openRes, tips] = await Promise.all([
          db
            .from('staff')
            .select('*')
            .eq('client_id', clientId)
            .order('sort_order', { ascending: true })
            .order('name', { ascending: true }),
          db.from('staff_pay').select('*').eq('client_id', clientId),
          db.from('staff_rates').select('*').eq('client_id', clientId),
          db
            .from('time_entries')
            .select('*')
            .eq('client_id', clientId)
            .lte('clock_in', endIso)
            .gte('clock_out', startIso),
          db
            .from('time_entries')
            .select('*')
            .eq('client_id', clientId)
            .is('clock_out', null)
            .lte('clock_in', endIso),
          sumPaidTips(clientId, from, to),
        ]);
        const err = staffRes.error ?? payRes.error ?? rateRes.error ?? closedRes.error ?? openRes.error;
        if (err) throw new Error(err.message);
        if (cancelled) return;
        const payById: Record<string, PayRow> = {};
        for (const row of payRes.data ?? []) payById[row.staff_id] = row;
        const ratesById: Record<string, RatePeriod[]> = {};
        for (const row of (rateRes.data ?? []) as RateRow[]) {
          (ratesById[row.staff_id] ??= []).push({
            effectiveFrom: row.effective_from,
            payType: asPayType(row.pay_type),
            rateAmd: row.rate_amd,
          });
        }
        const entries = new Map<string, EntryRow>();
        for (const row of [...(closedRes.data ?? []), ...(openRes.data ?? [])]) entries.set(row.id, row);
        const now = new Date();
        const people = (staffRes.data ?? []).map((person: StaffRow) => {
          const pay = payById[person.id];
          const punches: PunchSpan[] = [...entries.values()]
            .filter((e) => e.staff_id === person.id && e.note !== 'off')
            .map((e) => ({
              clockIn: new Date(e.clock_in),
              clockOut: e.clock_out ? new Date(e.clock_out) : null,
              breakMinutes: e.break_minutes,
            }));
          const history = ratesById[person.id] ?? [];
          const rates =
            history.length > 0
              ? history
              : pay
                ? [{ effectiveFrom: '1970-01-01', payType: asPayType(pay.pay_type), rateAmd: pay.rate_amd }]
                : [];
          const wage = wageForRange(rates, punches, from, to, now);
          return {
            id: person.id,
            name: person.name,
            onTimesheet: person.on_timesheet,
            minutes: wage.minutes,
            wageAmd: wage.wageAmd,
          };
        }).filter((person) => person.onTimesheet || person.minutes > 0);
        const shares = splitAmount(
          tips,
          people.map((person) => ({ id: person.id, weight: person.minutes })),
        );
        const totalMinutes = people.reduce((sum, person) => sum + person.minutes, 0);
        setTipPool(Math.round(tips));
        setRows(
          people
            .map((person) => {
              const tipAmd = shares.get(person.id) ?? 0;
              return {
                ...person,
                tipAmd,
                totalAmd: person.wageAmd + tipAmd,
                share: totalMinutes > 0 ? person.minutes / totalMinutes : 0,
              };
            })
            .sort((a, b) => b.totalAmd - a.totalAmd || a.name.localeCompare(b.name)),
        );
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : t('staffInsights.loadFailed');
        setError(msg === '__not_configured__' ? t('staffInsights.notConfigured') : msg);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, range, t]);

  const totals = rows.reduce(
    (acc, row) => {
      acc.minutes += row.minutes;
      acc.wageAmd += row.wageAmd;
      acc.tipAmd += row.tipAmd;
      acc.totalAmd += row.totalAmd;
      return acc;
    },
    { minutes: 0, wageAmd: 0, tipAmd: 0, totalAmd: 0 },
  );
  const colorOf = new Map(rows.map((row, index) => [row.id, chartColor(index)]));
  const paySlices = rows
    .filter((row) => row.wageAmd > 0)
    .map((row) => ({ id: row.id, name: row.name, value: row.wageAmd, color: colorOf.get(row.id) ?? chartColor(0) }));
  const tipSlices = rows
    .filter((row) => row.tipAmd > 0)
    .map((row) => ({ id: row.id, name: row.name, value: row.tipAmd, color: colorOf.get(row.id) ?? chartColor(0) }));

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('staffInsights.title')}
        subtitle={t('staffInsights.subtitle')}
        actions={<DateRange value={range} onChange={setRange} />}
      />

      {error && (
        <Callout tone="danger" title={t('staffInsights.couldntLoad')}>
          {error}
        </Callout>
      )}

      <Stats>
        <Stat label={t('staffInsights.hours')} value={formatHours(totals.minutes)} />
        <Stat label={t('staffInsights.wages')} value={money(totals.wageAmd)} />
        <Stat label={t('staffInsights.tipPool')} value={money(tipPool)} />
        <Stat label={t('staffInsights.totalPay')} value={money(totals.totalAmd)} />
      </Stats>
      <p className="muted" style={{ marginTop: -6 }}>
        {t('staffInsights.tipExplain')}
      </p>

      {loading ? (
        <div className="empty-state">{t('common.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">{t('staffInsights.empty')}</div>
      ) : (
        <>
          {totals.minutes === 0 && tipPool > 0 && <Callout tone="info">{t('staffInsights.noHours')}</Callout>}
          {(paySlices.length > 0 || tipSlices.length > 0) && (
            <div className="ui-grid-2">
              {paySlices.length > 0 && <SharePie title={t('staffInsights.chartTitle')} slices={paySlices} />}
              {tipSlices.length > 0 && <SharePie title={t('staffInsights.chartTips')} slices={tipSlices} />}
            </div>
          )}
          <div className="panel">
            <table className="table table-cards">
              <thead>
                <tr>
                  <th>{t('staffInsights.colName')}</th>
                  <th style={{ textAlign: 'right' }}>{t('staffInsights.colHours')}</th>
                  <th style={{ textAlign: 'right' }}>{t('staffInsights.colShare')}</th>
                  <th style={{ textAlign: 'right' }}>{t('staffInsights.colWage')}</th>
                  <th style={{ textAlign: 'right' }}>{t('staffInsights.colTip')}</th>
                  <th style={{ textAlign: 'right' }}>{t('staffInsights.colTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="table-cards-title" data-label={t('staffInsights.colName')}>
                      {row.name}
                    </td>
                    <td data-label={t('staffInsights.colHours')} style={{ textAlign: 'right' }}>
                      {formatHours(row.minutes)}
                    </td>
                    <td data-label={t('staffInsights.colShare')} style={{ textAlign: 'right' }}>
                      {(row.share * 100).toLocaleString(locale, { maximumFractionDigits: 1 })}%
                    </td>
                    <td data-label={t('staffInsights.colWage')} style={{ textAlign: 'right' }}>
                      {money(row.wageAmd)}
                    </td>
                    <td data-label={t('staffInsights.colTip')} style={{ textAlign: 'right' }}>
                      {money(row.tipAmd)}
                    </td>
                    <td data-label={t('staffInsights.colTotal')} style={{ textAlign: 'right' }}>
                      {money(row.totalAmd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function SharePie({
  title,
  slices,
}: {
  title: string;
  slices: { id: string; name: string; value: number; color: string }[];
}) {
  const { i18n } = useTranslation();
  const money = (n: number) => formatAmd(n, i18n.language);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  return (
    <div className="chart-card">
      <div className="chart-title">{title}</div>
      <ResponsiveContainer width="100%" height={168}>
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={46}
            outerRadius={68}
            paddingAngle={2}
            stroke="var(--surface)"
          >
            {slices.map((slice) => (
              <Cell key={slice.id} fill={slice.color} />
            ))}
          </Pie>
          <Tooltip contentStyle={CHART_TOOLTIP} formatter={(value) => money(Number(value))} />
        </PieChart>
      </ResponsiveContainer>
      <div className="pay-legend">
        {slices.map((slice) => (
          <div key={slice.id} className="pay-legend-row">
            <span className="pay-legend-dot" style={{ background: slice.color }} />
            <span className="pay-legend-name">{slice.name}</span>
            <span className="pay-legend-pct">
              {total > 0 ? `${Math.round((slice.value / total) * 100)}%` : '0%'}
            </span>
            <span className="pay-legend-val">{money(slice.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
